import type { ReactNode } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';
import { cn } from '../../lib/utils';

export default function ComboboxNumberField({
	label,
	placeholder,
	options,
	className,
	orientation,
	children,
}: {
	label: string;
	placeholder: string;
	search?: string;
	empty?: string;
	options: { value: number; label: string }[];
	variant?: 'default' | 'outline';
	children?: ReactNode;
	className?: string;
	orientation?: 'vertical' | 'horizontal';
}) {
	const field = useFieldContext<number | null>();
	const formReadOnly = useFormReadOnly();
	const isHorizontal = orientation === 'horizontal';
	const isOptional = useIsFieldOptional(field.name);

	return (
		<div
			className={cn(
				'flex flex-col gap-2',
				className,
				formReadOnly &&
					'pointer-events-none [&_button]:border-transparent [&_button]:px-0 [&_button]:focus-visible:ring-0 [&_[data-slot=select-trigger]>svg]:hidden',
			)}
		>
			<div className={isHorizontal ? 'flex items-start gap-3' : 'grid gap-2'}>
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						className={cn('flex items-center justify-between', isHorizontal && 'w-36 shrink-0')}
					/>
				)}
				<div className={cn(isHorizontal ? 'flex-1' : undefined)}>
					<Select
						value={field.state.value?.toString() ?? ''}
						onValueChange={(val) => field.handleChange(val === '' ? null : Number(val))}
					>
						<SelectTrigger id={field.name} aria-invalid={hasFieldError(field.state.meta)} className="w-full">
							<SelectValue placeholder={placeholder} />
						</SelectTrigger>
						<SelectContent>
							{options.map((option) => (
								<SelectItem key={option.value} value={option.value.toString()}>
									{option.label}
								</SelectItem>
							))}
							{children}
						</SelectContent>
					</Select>
					<FieldInfo fieldState={field.state} />
				</div>
			</div>
		</div>
	);
}
